import { useState } from 'react';
import { Loader2, AlertTriangle, Trash2, ListChecks } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Read-only census of every live Xero connection, flagging duplicate
// connections per tenant and connections to tenants outside the entities
// configured for Xero. Disconnect is gated by a per-row Preview/confirm
// step -- never bulk, and never a bare window.confirm -- see
// services/xero_connections_census.py for the backend contract.
export function XeroConnectionsCensusPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [previewing, setPreviewing] = useState(null); // { connectionId, message, connection } | { connectionId, error }
  const [previewBusy, setPreviewBusy] = useState(null); // connectionId currently loading a preview
  const [confirmBusy, setConfirmBusy] = useState(false);

  const fetchCensus = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.xeroConnectionsCensus();
      setData(d);
      if (d.error) {
        setError(d.error);
      }
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const startPreview = async (connectionId) => {
    setPreviewBusy(connectionId);
    setPreviewing(null);
    try {
      const { data: d } = await diagnosticsAPI.xeroConnectionsCensusDisconnectPreview(connectionId);
      if (d.error || !d.found) {
        setPreviewing({ connectionId, error: d.error || 'Connection not found.' });
      } else {
        setPreviewing({ connectionId, message: d.message, connection: d.connection });
      }
    } catch (err) {
      setPreviewing({ connectionId, error: getErrorMessage(err) });
    } finally {
      setPreviewBusy(null);
    }
  };

  const cancelPreview = () => setPreviewing(null);

  const confirmDisconnect = async (connectionId) => {
    setConfirmBusy(true);
    try {
      const { data: d } = await diagnosticsAPI.xeroConnectionsCensusDisconnectConfirm(connectionId);
      if (d.deleted) {
        toast.success(d.message || `Disconnected ${connectionId}`);
        setPreviewing(null);
        await fetchCensus();
      } else {
        toast.error(d.error || 'Disconnect was not confirmed by Xero.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setConfirmBusy(false);
    }
  };

  const cap = data?.cap ?? 25;
  const count = data?.count ?? 0;
  const headroom = data?.headroom ?? cap - count;
  const nearCap = !!data?.near_cap;
  const duplicateCount = data?.duplicate_tenant_count ?? 0;
  const unknownCount = data?.unknown_tenant_count ?? 0;
  const connections = data?.credentials?.[0]?.connections ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ListChecks className="h-5 w-5" />
          Xero Connections Census
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only census of live connections per credential, flagging duplicate connections per
          tenant and connections to tenants outside our configured entities. Disconnect requires a
          Preview step first, one connection at a time -- never bulk.
        </p>

        <Button
          variant="outline"
          size="sm"
          onClick={fetchCensus}
          disabled={loading}
          data-testid="xero-census-load"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Load Census
        </Button>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500" data-testid="xero-census-error">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {data && !error && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={nearCap ? 'destructive' : 'default'} className="text-xs" data-testid="xero-census-cap-badge">
                {count} / {cap} connections used
              </Badge>
              {nearCap && (
                <Badge variant="destructive" className="text-xs">
                  Near uncertified-app cap ({Math.max(headroom, 0)} slot{headroom !== 1 ? 's' : ''} left)
                </Badge>
              )}
              {duplicateCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {duplicateCount} duplicate connection{duplicateCount !== 1 ? 's' : ''}
                </Badge>
              )}
              {unknownCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {unknownCount} unknown-tenant connection{unknownCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>

            <p className="text-xs text-muted-foreground" data-testid="xero-census-message">
              {data.message}
            </p>

            {connections.length === 0 ? (
              <p className="text-sm text-muted-foreground">No connections registered.</p>
            ) : (
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 text-left">
                      <th className="px-3 py-2 font-medium">Tenant Name</th>
                      <th className="px-3 py-2 font-medium">Tenant ID</th>
                      <th className="px-3 py-2 font-medium">Connection ID</th>
                      <th className="px-3 py-2 font-medium">Created</th>
                      <th className="px-3 py-2 font-medium">Updated</th>
                      <th className="px-3 py-2 font-medium">Flags</th>
                      <th className="px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connections.map((c) => (
                      <tr key={c.connection_id} className="border-t" data-testid={`xero-census-row-${c.connection_id}`}>
                        <td className="px-3 py-2">{c.tenant_name || '(unnamed)'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.tenant_id}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.connection_id}</td>
                        <td className="px-3 py-2 text-xs">
                          {c.created_date_utc ? new Date(c.created_date_utc).toLocaleString() : ''}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {c.updated_date_utc ? new Date(c.updated_date_utc).toLocaleString() : ''}
                        </td>
                        <td className="px-3 py-2 space-x-1">
                          {c.is_duplicate && <Badge variant="destructive" className="text-xs">duplicate</Badge>}
                          {c.is_unknown_tenant && <Badge variant="destructive" className="text-xs">unknown tenant</Badge>}
                        </td>
                        <td className="px-3 py-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startPreview(c.connection_id)}
                            disabled={previewBusy === c.connection_id}
                            data-testid={`xero-census-disconnect-${c.connection_id}`}
                          >
                            {previewBusy === c.connection_id ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Trash2 className="h-3 w-3 mr-1" />
                            )}
                            Disconnect
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {previewing && (
              <div
                className="border border-red-400 rounded p-3 space-y-2 bg-red-50 dark:bg-red-950/20"
                data-testid="xero-census-preview-panel"
              >
                {previewing.error ? (
                  <p className="text-sm text-red-600">{previewing.error}</p>
                ) : (
                  <>
                    <p className="text-sm">{previewing.message}</p>
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => confirmDisconnect(previewing.connectionId)}
                        disabled={confirmBusy}
                        data-testid="xero-census-confirm-disconnect"
                      >
                        {confirmBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                        Confirm Disconnect
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={cancelPreview}
                        disabled={confirmBusy}
                        data-testid="xero-census-cancel-disconnect"
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
